import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getManagedCategories, createCategory, deleteCategory } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Tag, Loader2 } from 'lucide-react';

import { useDepartments } from '../hooks/useDepartments';

const CategoryManagementPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'Super Admin';
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCatName, setNewCatName] = useState('');
  const [selectedDept, setSelectedDept] = useState(isSuperAdmin ? 'Marketing' : user?.department || '');
  const [adding, setAdding] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getManagedCategories(selectedDept || undefined);
      setCategories(res.data);
    } catch { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  }, [selectedDept]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleAdd = async () => {
    if (!newCatName.trim()) return toast.error('Enter a category name');
    if (!selectedDept) return toast.error('Select a department');
    try {
      setAdding(true);
      await createCategory({ name: newCatName.trim(), department: selectedDept });
      toast.success('Category added');
      setNewCatName('');
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add category');
    } finally { setAdding(false); }
  };

  const handleDelete = async (id, name) => {
    try {
      await deleteCategory(id);
      toast.success(`"${name}" deleted`);
      fetchCategories();
    } catch { toast.error('Failed to delete category'); }
  };

  if (user?.role !== 'Super Admin' && user?.role !== 'Admin') {
    return <div className="text-center py-12 text-muted-foreground">You don't have permission to manage categories.</div>;
  }

  return (
    <div className="space-y-6" data-testid="category-management-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Category Management</h1>
        <p className="text-muted-foreground text-sm mt-1">Add or remove task categories for each department</p>
      </div>

      {isSuperAdmin && (
        <Select value={selectedDept} onValueChange={setSelectedDept}>
          <SelectTrigger className="w-64" data-testid="cat-dept-filter">
            <SelectValue placeholder="Select department" />
          </SelectTrigger>
          <SelectContent>
            {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Tag className="h-5 w-5" />
            {selectedDept} Categories
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              data-testid="new-category-input"
              placeholder="New category name..."
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              className="max-w-sm"
            />
            <Button data-testid="add-category-btn" onClick={handleAdd} disabled={adding} size="sm">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : categories.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4">No categories yet. Add one above.</p>
          ) : (
            <div className="grid gap-2">
              {categories.map(cat => (
                <div key={cat.id} className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`category-item-${cat.id}`}>
                  <div className="flex items-center gap-3">
                    <Tag className="h-4 w-4 text-primary/60" />
                    <span className="font-medium text-sm">{cat.name}</span>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-cat-${cat.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Category</AlertDialogTitle>
                        <AlertDialogDescription>Are you sure you want to delete "{cat.name}"? This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(cat.id, cat.name)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CategoryManagementPage;
